import React, { useState } from 'react';

import { PartyPopper, StickyNote } from 'lucide-react';

import UpsellModal from 'common/modals/UpsellModal';
import NoticeBar from 'common/uiV3/Composite/NoticeBar';
import { dayjs } from 'common/util/dayjsUtils';
import { isNotNil } from 'common/util/isNil';
import { StarterPlanID } from 'common/util/isStarter';

import type { Company } from 'common/api/endpoints/companies';

enum Modal {
  Upsell = 'upsell',
}

const AdminV2PostLimitNotice = ({
  limits,
  stats,
}: {
  limits: Company['limits'];
  stats: Company['stats'];
}) => {
  const { activePostCount, activePostLimitLockoutAt } = stats;
  const [modal, setModal] = useState<Modal | null>(null);

  const Upsell =
    modal === Modal.Upsell ? (
      <UpsellModal
        cta="Unlimited active posts"
        feature="limits.activePosts"
        onClose={() => setModal(null)}
        onUpsell={() => setModal(null)}
        show={modal === Modal.Upsell}
        planID={StarterPlanID}
      />
    ) : null;

  if (isNotNil(limits.activePosts) && activePostCount > limits.activePosts) {
    let countdown = 'within a few days';
    if (activePostLimitLockoutAt) {
      const lockoutAt = dayjs(activePostLimitLockoutAt);
      const now = dayjs();
      const daysTil = lockoutAt.diff(now, 'days');
      countdown = daysTil > 1 ? `within ${daysTil} days` : 'today';
    }

    return (
      <>
        <NoticeBar
          variant="neutral"
          icon={PartyPopper}
          message={`You've reached ${activePostCount}/100 active posts! Mark posts as complete ${countdown} or upgrade for limitless posts!`}
          action={{
            type: 'button',
            label: 'Get More Time',
            onClick: () => setModal(Modal.Upsell),
          }}
        />
        {Upsell}
      </>
    );
  } else if (isNotNil(limits.activePosts) && activePostCount > limits.activePosts * 0.75) {
    const postsLeft = limits.activePosts - activePostCount;

    return (
      <>
        <NoticeBar
          variant="neutral"
          icon={StickyNote}
          message={`You're growing! You can track ${postsLeft} more posts for free`}
          action={{
            type: 'button',
            label: 'Get More',
            onClick: () => setModal(Modal.Upsell),
          }}
        />
        {Upsell}
      </>
    );
  }

  return Upsell;
};

export default AdminV2PostLimitNotice;
